/**
 * SmartCalculators — universal field-takeoff calculators.
 *
 * Replaces the previous trade → category → template-calculator navigator.
 * The 8 calculators here aren't category-scoped: mulch volume math serves
 * planting AND hardscape base AND drainage; concrete math serves footings
 * across half the categories. Flat grid picker is the right shape.
 */

import React, { useMemo, useState } from 'react';
import { EstimateItem } from '../../types';
import { useEstimates } from '../../contexts/EstimatesContext';
import { useIdioma } from '../../i18n/idioma';
import CalculatorGrid from './calculators/CalculatorGrid';
import CalculatorShell from './calculators/CalculatorShell';
import { CALCULATORS } from './calculators/registry';

interface SmartCalculatorsProps {
  onAddTemplateItems: (items: EstimateItem[], template: { id: string; name: string }, targetZoneId?: string) => void;
  targetMarginPercent: number;
  materialMarkupPercent: number;
  activeTradeId?: string;
}

const SmartCalculators: React.FC<SmartCalculatorsProps> = ({ onAddTemplateItems, targetMarginPercent, materialMarkupPercent, activeTradeId }) => {
  const [activeId, setActiveId] = useState<string | null>(null);
  const { pricebook } = useEstimates();
  const { t } = useIdioma();

  // El mismo resolvedor que usa SmartTemplates: el pricebook del taller
  // indexado por id de catálogo, para que las calculadoras puedan pre-valorar
  // las partidas que sueltan.
  //
  // Igual que allá, solo entran costos reales (> 0). Aquí los consumidores usan
  // `.get(id) ?? 0`, así que hoy una entrada en $0 es inofensiva, pero se
  // mantienen los dos índices idénticos para que no se separen el día que
  // alguien copie este bloque a un sitio que sí pregunte con `.has()`.
  const pricebookByHint = useMemo(() => {
    const map = new Map<string, number>();
    for (const item of pricebook) {
      if (!item.catalogItemId) continue;
      const cost = Number(item.cost);
      if (Number.isFinite(cost) && cost > 0) map.set(item.catalogItemId, cost);
    }
    return map;
  }, [pricebook]);

  const active = activeId ? CALCULATORS.find(c => c.id === activeId) : null;

  if (active) {
    const { Component, name, description, Icon } = active;
    return (
      <div className="flex flex-col h-full bg-white">
        <CalculatorShell
          name={name}
          description={description}
          Icon={Icon}
          onBack={() => setActiveId(null)}
        >
          <Component
            onAddTemplateItems={onAddTemplateItems}
            pricebookByHint={pricebookByHint}
            targetMarginPercent={targetMarginPercent}
            materialMarkupPercent={materialMarkupPercent}
          />
        </CalculatorShell>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0 bg-acero-50 p-4 gap-3">
      <div className="px-1">
        <h3 className="label-ui">{t('calc.campo.titulo')}</h3>
        <p className="text-[11px] text-acero-500 mt-1 leading-snug">
          {t('calc.campo.sub')}
        </p>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1 pb-10">
        <CalculatorGrid onSelect={(id) => setActiveId(id)} activeTradeId={activeTradeId} />
      </div>
    </div>
  );
};

export default SmartCalculators;
